import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, ShieldQuestion, EyeOff, Flag, SlidersHorizontal, Building2, Mail, Phone } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TYPE_OPTIONS = [
  { v: "peer_support", l: "Peer support" }, { v: "nhs_service", l: "NHS drug & alcohol" },
  { v: "private_rehab", l: "Private rehab" }, { v: "charity", l: "Charity" },
  { v: "online", l: "Online / remote" }, { v: "other", l: "Other service" },
];

export default function Admin() {
  const [pending, setPending] = useState([]);
  const [reports, setReports] = useState([]);
  const [review, setReview] = useState([]);
  const [claims, setClaims] = useState([]);

  const load = () => {
    api.get("/admin/centres/pending").then(({ data }) => setPending(data.centres)).catch((e) => toast.error(apiErr(e)));
    api.get("/admin/reports").then(({ data }) => setReports(data.reports)).catch(() => {});
    api.get("/admin/centres/review").then(({ data }) => setReview(data.centres)).catch(() => {});
    api.get("/admin/claims").then(({ data }) => setClaims(data.claims)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const claimAct = async (id, action) => {
    try {
      await api.post(`/admin/claims/${id}/${action}`);
      toast.success(action === "approve" ? "Claim approved — the provider can now manage this listing." : "Claim rejected.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const centreAct = async (id, action) => {
    try {
      if (action === "approve") await api.post(`/admin/centres/${id}/approve`);
      if (action === "verify") await api.post(`/admin/centres/${id}/verify`);
      if (action === "reject") await api.delete(`/admin/centres/${id}`);
      toast.success(action === "reject" ? "Suggestion removed." : "Done.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const categorise = async (id, type) => {
    try {
      await api.post(`/admin/centres/${id}/categorise`, { type });
      toast.success("Category saved.");
      setReview((rs) => rs.filter((r) => r.id !== id));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reportAct = async (id, action) => {
    try {
      await api.post(`/admin/reports/${id}/${action}`);
      toast.success(action === "hide" ? "Content hidden." : "Report dismissed.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Admin</h1>
        <p className="mt-2 text-muted-foreground">Keep the directory trustworthy and the community safe.</p>
      </div>

      <Tabs defaultValue="centres">
        <TabsList className="rounded-full bg-muted p-1">
          <TabsTrigger value="centres" data-testid="tab-centres" className="rounded-full">Centre approvals {pending.length > 0 && `(${pending.length})`}</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-full">Moderation {reports.length > 0 && `(${reports.length})`}</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review" className="rounded-full">Needs review {review.length > 0 && `(${review.length})`}</TabsTrigger>
          <TabsTrigger value="claims" data-testid="tab-claims" className="rounded-full">Claims {claims.length > 0 && `(${claims.length})`}</TabsTrigger>
        </TabsList>

        <TabsContent value="centres" className="mt-4">
          {pending.length === 0 ? (
            <Empty icon={ShieldQuestion} title="No pending suggestions" desc="New centre submissions will appear here for review." />
          ) : (
            <div className="space-y-4" data-testid="pending-list">
              {pending.map((c) => (
                <div key={c.id} className="rounded-2xl border border-border bg-white p-6 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-head text-lg font-semibold text-[#2C363F]">{c.name}</h3>
                      <p className="text-sm text-muted-foreground">{c.type} · {c.country}</p>
                      <p className="mt-2 text-sm text-[#2C363F]">{c.address}{c.postcode ? `, ${c.postcode}` : ""}</p>
                      {c.description && <p className="mt-2 text-sm text-muted-foreground">{c.description}</p>}
                      <p className="mt-2 text-xs text-muted-foreground">{c.phone} {c.website}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`approve-${c.id}`} onClick={() => centreAct(c.id, "approve")} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Check className="mr-1 h-4 w-4" /> Approve</Button>
                      <Button data-testid={`reject-${c.id}`} onClick={() => centreAct(c.id, "reject")} variant="outline" className="rounded-full border-[#E8907E] text-[#8a4436]"><X className="mr-1 h-4 w-4" /> Reject</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          {reports.length === 0 ? (
            <Empty icon={Flag} title="Nothing to moderate" desc="Reported posts and replies will appear here." />
          ) : (
            <div className="space-y-4" data-testid="reports-list">
              {reports.map((r) => (
                <div key={r.id} className="rounded-2xl border border-border bg-white p-6 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <span className="inline-block rounded-full bg-[#E8907E]/15 px-2.5 py-0.5 text-xs font-medium text-[#8a4436]">Reported {r.kind}</span>
                      <p className="mt-2 text-sm text-[#2C363F]">{r.content}</p>
                      {r.reason && <p className="mt-1 text-xs text-muted-foreground">Reason: {r.reason}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`hide-${r.id}`} onClick={() => reportAct(r.id, "hide")} className="rounded-full bg-[#8a4436] hover:bg-[#6f3529]"><EyeOff className="mr-1 h-4 w-4" /> Hide</Button>
                      <Button data-testid={`dismiss-${r.id}`} onClick={() => reportAct(r.id, "dismiss")} variant="outline" className="rounded-full">Dismiss</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="review" className="mt-4">
          {review.length === 0 ? (
            <Empty icon={SlidersHorizontal} title="Nothing to review" desc="Imported listings we couldn't auto-categorise will appear here so you can set the right service type." />
          ) : (
            <div className="space-y-4" data-testid="review-list">
              {review.map((c) => (
                <div key={c.id} className="rounded-2xl border border-border bg-white p-6 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-head text-lg font-semibold text-[#2C363F]">{c.name}</h3>
                        {c.source && <span className="rounded-full bg-[#8FB8DE]/25 px-2 py-0.5 text-xs font-medium text-[#2C4763]">{c.source}</span>}
                      </div>
                      <p className="mt-1 text-sm text-[#2C363F]">{c.address}{c.postcode ? `, ${c.postcode}` : ""}</p>
                      <p className="mt-1 text-xs text-muted-foreground">Current: <span className="font-medium">{c.type}</span>{c.type_raw ? ` · source type: ${c.type_raw}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select onValueChange={(v) => categorise(c.id, v)}>
                        <SelectTrigger data-testid={`review-type-${c.id}`} className="w-48 rounded-full"><SelectValue placeholder="Set service type…" /></SelectTrigger>
                        <SelectContent>
                          {TYPE_OPTIONS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button data-testid={`review-remove-${c.id}`} onClick={() => centreAct(c.id, "reject")} variant="outline" className="rounded-full border-[#E8907E] text-[#8a4436]"><X className="mr-1 h-4 w-4" /> Remove</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="claims" className="mt-4">
          {claims.length === 0 ? (
            <Empty icon={Building2} title="No claims to review" desc="When a recovery service claims its listing, the request will appear here to verify." />
          ) : (
            <div className="space-y-4" data-testid="claims-list">
              {claims.map((cl) => (
                <div key={cl.id} className="rounded-2xl border border-border bg-white p-6 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Building2 className="h-4 w-4 text-[#4A7c59]" />
                        <h3 className="font-head text-lg font-semibold text-[#2C363F]">{cl.centre_name}</h3>
                      </div>
                      <p className="mt-2 text-sm text-[#2C363F]"><span className="font-medium">{cl.contact_name}</span> — {cl.role}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {cl.contact_email}</span>
                        {cl.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {cl.phone}</span>}
                      </p>
                      {cl.note && <p className="mt-2 rounded-lg bg-muted/60 p-2 text-sm text-muted-foreground">{cl.note}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`claim-approve-${cl.id}`} onClick={() => claimAct(cl.id, "approve")} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Check className="mr-1 h-4 w-4" /> Approve</Button>
                      <Button data-testid={`claim-reject-${cl.id}`} onClick={() => claimAct(cl.id, "reject")} variant="outline" className="rounded-full border-[#E8907E] text-[#8a4436]"><X className="mr-1 h-4 w-4" /> Reject</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Empty = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center">
    <Icon className="mx-auto h-10 w-10 text-muted-foreground" />
    <p className="mt-3 font-head font-semibold text-[#2C363F]">{title}</p>
    <p className="text-sm text-muted-foreground">{desc}</p>
  </div>
);
